import { useState, type ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import DeleteOutlined from '@mui/icons-material/DeleteOutlined';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { isApiError, links, rowsOf, type DatasetField } from '@eis/core/reporting';
import {
  ConfirmAction,
  ErrorState,
  PageHeader,
  PageLayout,
  RequirePermission,
  cardSurfaceSx,
} from '@eis/patterns';
import {
  JoinEditor,
  QueryRunState,
  joinNeedsCast,
  visuallyHidden,
  type JoinField,
} from '@eis/patterns/reporting';
import { reportingPermissions as p } from '../../../permissions';
import { codeMessage } from '../../../lib/codes';
import { useReportWithDataset } from '../../reports-list/ui/ReportsListPage';
import {
  asDatasetDocument,
  duplicateAliases,
  type DatasetDocument,
} from '../../datasets/model/datasets';
import {
  isConflictError,
  reportingReasonOf,
  useCustomDataset,
  useDataset,
  useDeleteCustomDataset,
  usePreviewCustomDataset,
  usePublishCustomDatasetDraft,
  usePutCustomDatasetDraft,
  useQueryPolling,
} from '../api/builder';
import { addSource, removeSource, setAlias, toggleField } from '../model/builder';
import { DatasetPicker } from '../../reports-list/ui/DatasetPicker';
import { DatasetPreview } from '../../datasets/ui/DatasetPreview';

export interface DatasetBuilderPageProps {
  readonly code: string;
}

/** Fields of up to four views, fetched once each (the hooks stay in a fixed order). */
function useSourceFields(
  doc: DatasetDocument | undefined,
): (alias: string) => readonly DatasetField[] {
  const view = (i: number): string | undefined => doc?.sources[i]?.view;
  const a = useDataset(view(0));
  const b = useDataset(view(1));
  const c = useDataset(view(2));
  const d = useDataset(view(3));
  return (alias: string): readonly DatasetField[] =>
    ({ a, b, c, d })[alias as 'a' | 'b' | 'c' | 'd']?.data?.fields ?? [];
}

/**
 * A custom dataset, built, not typed (SDD-024 S-1…S-6; mockup board 8): up to four views, INNER or
 * LEFT joins on field pairs with the type check of S-3 (a mismatch only with an explicit CAST), the
 * fields under unique aliases, a five-row preview through the query pipeline, and Publish — which
 * needs that preview of the draft as it is (S-5). Published versions are what reports pin; a new
 * one never moves a report (R-6).
 */
export function DatasetBuilderPage({ code }: DatasetBuilderPageProps): ReactNode {
  const { t } = useTranslation('reporting');
  const head = useCustomDataset(code);
  const [generation, setGeneration] = useState(0);
  const data = head.data?.dataset;
  const start =
    data === undefined
      ? undefined
      : (asDatasetDocument(data.draft) ?? asDatasetDocument(data.document));
  if (head.isLoading) {
    return (
      <PageLayout header={<PageHeader title={t('builder.breadcrumb')} />}>
        <Skeleton variant="rounded" height={420} />
      </PageLayout>
    );
  }
  if (head.isError || data === undefined || start === undefined) {
    return (
      <PageLayout header={<PageHeader title={t('builder.breadcrumb')} />}>
        <ErrorState
          description={
            isApiError(head.error) && head.error.status === 404
              ? t('builder.notFound')
              : t('builder.loadError')
          }
          onRetry={() => void head.refetch()}
        />
      </PageLayout>
    );
  }
  return (
    <BuilderBody
      key={generation}
      code={code}
      start={start}
      initialEtag={{
        ...(data.etag === undefined ? {} : { etag: data.etag }),
        ...(data.version === undefined ? {} : { version: data.version }),
      }}
      onReload={() => {
        void head.refetch().then(() => {
          setGeneration((g) => g + 1);
        });
      }}
    />
  );
}

interface BuilderBodyProps {
  readonly code: string;
  readonly start: DatasetDocument;
  readonly initialEtag: { readonly etag?: string; readonly version?: string };
  readonly onReload: () => void;
}

function BuilderBody({ code, start, initialEtag, onReload }: BuilderBodyProps): ReactNode {
  const { t } = useTranslation('reporting');
  const navigate = useNavigate();
  const head = useCustomDataset(code);
  const save = usePutCustomDatasetDraft();
  const preview = usePreviewCustomDataset();
  const publish = usePublishCustomDatasetDraft();
  const remove = useDeleteCustomDataset();
  const [doc, setDoc] = useState<DatasetDocument>(start);
  const [etag, setEtag] = useState<{ etag?: string; version?: string }>(initialEtag);
  const [savedJson, setSavedJson] = useState(() => JSON.stringify(start));
  const [previewId, setPreviewId] = useState<string | undefined>(undefined);
  const [publishedAs, setPublishedAs] = useState<number | undefined>(undefined);
  const [conflict, setConflict] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const reportWith = useReportWithDataset();
  const [newView, setNewView] = useState('');
  const polling = useQueryPolling(previewId);
  const fieldsOf = useSourceFields(doc);

  const persist = async (
    next: DatasetDocument,
  ): Promise<{ etag?: string; version?: string } | undefined> => {
    try {
      const saved = await save.mutateAsync({
        code,
        document: next as unknown as Record<string, unknown>,
        etag: etag.etag,
        version: etag.version,
      });
      const moved = {
        ...(saved.etag === undefined ? {} : { etag: saved.etag }),
        ...(saved.version === undefined ? {} : { version: saved.version }),
      };
      setEtag(moved);
      setSavedJson(JSON.stringify(next));
      return moved;
    } catch (error) {
      if (isConflictError(error)) setConflict(true);
      return undefined;
    }
  };

  const dataset = head.data?.dataset;
  const editable = dataset?.editable === true;

  if (dataset === undefined) return null;

  const dirty = JSON.stringify(doc) !== savedJson;
  const nextVersion = (publishedAs ?? dataset.publishedVersion ?? 0) + 1;
  const asJoinFields = (alias: string): JoinField[] =>
    fieldsOf(alias).map((f) => ({ name: f.name ?? '', type: f.type ?? 'STRING' }));
  const castMissing = (doc.joins ?? []).some((j) => joinNeedsCast(j, asJoinFields));
  const duplicates = duplicateAliases(doc);
  const query = polling.query;
  const previewRows = rowsOf(query?.rows);
  const failure = query?.error?.code;
  const publishReason =
    publish.error === null || isConflictError(publish.error)
      ? undefined
      : reportingReasonOf(publish.error);
  const removeReason = remove.error === null ? undefined : reportingReasonOf(remove.error);
  const usage = (dataset.usage ?? []).filter((u) => (u.reports ?? 0) > 0);

  const header = (
    <PageHeader
      title={doc.name}
      breadcrumbs={[
        { label: t('datasets.title'), to: links.reporting.datasets() },
        { label: doc.name },
        { label: t('builder.breadcrumb') },
      ]}
      actions={
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
          {(publishedAs ?? dataset.publishedVersion ?? 0) > 0 && (
            <Chip
              size="small"
              label={t('builder.published', {
                version: publishedAs ?? dataset.publishedVersion ?? 0,
              })}
            />
          )}
          {usage.map((u) => (
            <Chip
              key={u.version}
              size="small"
              variant="outlined"
              label={t('builder.inUse', { count: u.reports ?? 0, version: u.version ?? 0 })}
            />
          ))}
          <RequirePermission anyOf={[p.datasets.manage]}>
            <Button
              size="small"
              variant="outlined"
              color="error"
              disabled={!editable || usage.length > 0}
              onClick={() => {
                setArchiving(true);
              }}
            >
              {t('builder.archive')}
            </Button>
          </RequirePermission>
          {(publishedAs ?? dataset.publishedVersion ?? 0) > 0 && (
            <RequirePermission anyOf={[p.reports.create]}>
              <Button
                size="small"
                variant="outlined"
                disabled={reportWith.isPending}
                onClick={() => {
                  reportWith.start(
                    { kind: 'CUSTOM', code, version: publishedAs ?? dataset.publishedVersion ?? 1 },
                    doc.name,
                  );
                }}
              >
                {t('builder.reportWith')}
              </Button>
            </RequirePermission>
          )}
          <Button
            size="small"
            variant="contained"
            disabled={
              !editable || publish.isPending || conflict || castMissing || duplicates.length > 0
            }
            onClick={() => {
              void (async () => {
                let current = etag;
                if (dirty) {
                  const moved = await persist(doc);
                  if (moved === undefined) return;
                  current = moved;
                }
                publish.mutate(
                  { code, etag: current.etag, version: current.version },
                  {
                    onSuccess: (next) => {
                      setPublishedAs(next.publishedVersion ?? nextVersion);
                      setEtag({
                        ...(next.etag === undefined ? {} : { etag: next.etag }),
                        ...(next.version === undefined ? {} : { version: next.version }),
                      });
                    },
                    onError: (error) => {
                      if (isConflictError(error)) setConflict(true);
                    },
                  },
                );
              })();
            }}
          >
            {t('builder.publish', { version: nextVersion })}
          </Button>
        </Stack>
      }
    />
  );

  return (
    <PageLayout header={header}>
      <Stack spacing={2}>
        {conflict && (
          <Alert
            severity="warning"
            action={
              <Button size="small" color="inherit" onClick={onReload}>
                {t('editor.reload')}
              </Button>
            }
          >
            <AlertTitle>{t('editor.conflictTitle')}</AlertTitle>
            {t('editor.conflictBody')}
          </Alert>
        )}
        {publishReason !== undefined && (
          <Alert severity="warning">
            <AlertTitle>{codeMessage(t, publishReason).title}</AlertTitle>
            {codeMessage(t, publishReason).hint}
          </Alert>
        )}
        {removeReason !== undefined && (
          <Alert severity="error">{codeMessage(t, removeReason).title}</Alert>
        )}
        <Paper variant="outlined" sx={(theme) => ({ p: 2, ...cardSurfaceSx(theme) })}>
          <Typography variant="subtitle2" component="h2" sx={{ mb: 1 }}>
            {t('builder.sources')}
          </Typography>
          <Stack spacing={2}>
            {doc.sources.map((source) => {
              const join = (doc.joins ?? []).find((j) => j.right === source.alias);
              return (
                <Paper key={source.alias} variant="outlined" sx={{ p: 1.5 }}>
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ alignItems: 'center', mb: join === undefined ? 0 : 1 }}
                  >
                    <Chip size="small" label={source.alias.toUpperCase()} />
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {source.view}
                    </Typography>
                    {join === undefined ? (
                      <Typography variant="caption" color="text.secondary">
                        {t('builder.base')}
                      </Typography>
                    ) : (
                      editable && (
                        <IconButton
                          size="small"
                          aria-label={t('builder.removeSource', {
                            alias: source.alias.toUpperCase(),
                          })}
                          onClick={() => {
                            setDoc(removeSource(doc, source.alias));
                          }}
                        >
                          <DeleteOutlined fontSize="small" />
                        </IconButton>
                      )
                    )}
                  </Stack>
                  {join !== undefined && (
                    <JoinEditor
                      join={join}
                      fieldsOf={asJoinFields}
                      readOnly={!editable}
                      onChange={(next) => {
                        setDoc({
                          ...doc,
                          joins: (doc.joins ?? []).map((j) =>
                            j.right === source.alias ? next : j,
                          ),
                        });
                      }}
                    />
                  )}
                </Paper>
              );
            })}
            {editable && doc.sources.length < 4 && (
              <Stack spacing={1}>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                  <Box sx={{ minWidth: 280, flex: 1 }}>
                    <DatasetPicker
                      catalogOnly
                      label={t('builder.view')}
                      value={newView === '' ? '' : `CATALOG:${newView}`}
                      onChange={(option) => {
                        setNewView(option?.ref.kind === 'CATALOG' ? option.ref.name : '');
                      }}
                    />
                  </Box>
                  <Button
                    size="small"
                    disabled={newView === ''}
                    onClick={() => {
                      const first =
                        doc.joins?.[0]?.on[0]?.leftField ?? fieldsOf('a')[0]?.name ?? '';
                      setDoc(addSource(doc, newView, first));
                      setNewView('');
                    }}
                  >
                    {t('builder.addSource')}
                  </Button>
                </Stack>
                {newView !== '' && <DatasetPreview name={newView} />}
              </Stack>
            )}
          </Stack>
        </Paper>
        <Paper variant="outlined" sx={(theme) => ({ p: 2, ...cardSurfaceSx(theme) })}>
          <Stack direction="row" sx={{ alignItems: 'baseline', justifyContent: 'space-between' }}>
            <Typography variant="subtitle2" component="h2">
              {t('builder.fields')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('builder.selected', { count: doc.fields.length })}
            </Typography>
          </Stack>
          <Table size="small" aria-label={t('builder.fields')}>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Box component="span" sx={visuallyHidden}>
                    {t('builder.selected', { count: doc.fields.length })}
                  </Box>
                </TableCell>
                <TableCell>{t('builder.columns.source')}</TableCell>
                <TableCell>{t('builder.columns.field')}</TableCell>
                <TableCell>{t('builder.columns.alias')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {doc.fields.map((f) => (
                <TableRow key={`${f.source}:${f.field}`}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked
                      disabled={!editable}
                      slotProps={{
                        input: { 'aria-label': t('builder.include', { field: f.field }) },
                      }}
                      onChange={() => {
                        setDoc(toggleField(doc, f.source, f.field, false));
                      }}
                    />
                  </TableCell>
                  <TableCell>{f.source.toUpperCase()}</TableCell>
                  <TableCell>{f.field}</TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      value={f.alias}
                      disabled={!editable}
                      error={duplicates.includes(f.alias)}
                      slotProps={{
                        htmlInput: { 'aria-label': `${t('builder.columns.alias')}: ${f.field}` },
                      }}
                      onChange={(event) => {
                        setDoc(setAlias(doc, f.source, f.field, event.target.value));
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {editable && (
            <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: 'wrap', rowGap: 1 }}>
              {doc.sources.map((s) => (
                <TextField
                  key={s.alias}
                  select
                  size="small"
                  label={t('builder.selectAll', { alias: s.alias.toUpperCase() })}
                  value=""
                  sx={{ minWidth: 220 }}
                  onChange={(event) => {
                    setDoc(toggleField(doc, s.alias, event.target.value, true));
                  }}
                >
                  {fieldsOf(s.alias)
                    .filter(
                      (f) => !doc.fields.some((x) => x.source === s.alias && x.field === f.name),
                    )
                    .map((f) => (
                      <MenuItem key={f.name} value={f.name}>
                        {f.name}
                      </MenuItem>
                    ))}
                </TextField>
              ))}
            </Stack>
          )}
        </Paper>
        <Paper variant="outlined" sx={(theme) => ({ p: 2, ...cardSurfaceSx(theme) })}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle2" component="h2" sx={{ flex: 1 }}>
              {query?.state === 'SUCCEEDED'
                ? t('builder.previewRows', { count: previewRows.length })
                : t('editor.preview')}
            </Typography>
            <RequirePermission anyOf={[p.queries.run]}>
              <Button
                size="small"
                variant="outlined"
                disabled={!editable || preview.isPending || castMissing}
                onClick={() => {
                  void (async () => {
                    if (dirty && (await persist(doc)) === undefined) return;
                    preview.mutate(
                      { code, document: doc as unknown as Record<string, unknown> },
                      {
                        onSuccess: (q) => {
                          setPreviewId(q.id);
                        },
                      },
                    );
                  })();
                }}
              >
                {t('builder.previewRun')}
              </Button>
            </RequirePermission>
          </Stack>
          {previewId !== undefined && (
            <QueryRunState
              phase={polling.phase === 'IDLE' ? 'QUEUED' : polling.phase}
              elapsedSeconds={polling.elapsedSeconds}
              queryId={previewId}
              empty={query?.state === 'SUCCEEDED' && previewRows.length === 0}
              {...(failure === undefined
                ? {}
                : {
                    errorCode: failure,
                    errorTitle: codeMessage(t, failure).title,
                    errorHint: codeMessage(t, failure).hint,
                  })}
              {...(query?.error?.message === undefined
                ? {}
                : { engineMessage: query.error.message })}
            />
          )}
          {query?.state === 'SUCCEEDED' && previewRows.length > 0 && (
            <Table size="small" aria-label={t('editor.preview')} data-testid="dataset-preview">
              <TableHead>
                <TableRow>
                  {(query.columns ?? []).map((c) => (
                    <TableCell key={c.key}>{c.label}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {previewRows.map((row, i) => (
                  <TableRow key={String(i)}>
                    {row.map((cell, j) => (
                      <TableCell key={String(j)}>{cell ?? '—'}</TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Paper>
      </Stack>
      <ConfirmAction
        open={archiving}
        title={t('datasets.archiveTitle', { name: doc.name })}
        description={t('datasets.archiveBody')}
        severity="destructive"
        confirmLabel={t('builder.archive')}
        busy={remove.isPending}
        onCancel={() => {
          setArchiving(false);
        }}
        onConfirm={() => {
          remove.mutate(
            { code, etag: etag.etag, version: etag.version },
            {
              onSuccess: () => {
                setArchiving(false);
                void navigate({ href: links.reporting.datasets() });
              },
            },
          );
        }}
      />
    </PageLayout>
  );
}
